import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, CircleSlash, Loader2, MessageCircle, Package } from "lucide-react";
import { cn } from "@/lib/utils";
import type { OrderDetail, OrderPurchaseListItem } from "@/lib/types";
import {
  fetchAdminOrder,
  transitionOrder,
  updatePurchaseListItem,
} from "@/lib/ordersApi";
import { getApiErrorMessage } from "@/lib/api";
import { orderTitle } from "@/lib/orderUtils";
import { CLIENT_TYPE_LABELS } from "@/lib/clientTypeLabels";
import { PurchaseDecisionModal } from "@/components/admin/PurchaseDecisionModal";
import { AdvanceConfirmDialog } from "@/components/admin/AdvanceConfirmDialog";

type Decision = "total" | "partial" | "stock" | "none" | "pending";

function decisionOf(item: OrderPurchaseListItem): Decision {
  if (item.purchased) return "total";
  if (item.useStock) return "stock";
  if (item.purchasedQuantity != null && item.purchasedQuantity > 0) return "partial";
  if (item.reason) return "none";
  return "pending";
}

const DECISION_LABEL: Record<Exclude<Decision, "pending">, string> = {
  total: "Comprado",
  partial: "Parcial",
  stock: "Em estoque",
  none: "Não comprado",
};

interface Props {
  orderId: string | null;
  onClose: () => void;
  // Lets the parent (Kanban) refresh its data when an item is updated or pedido transitions.
  onOrderChanged?: (order: OrderDetail) => void;
}

export function BuyerOrderDialog({ orderId, onClose, onOrderChanged }: Props) {
  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [advanceOpen, setAdvanceOpen] = useState(false);
  const [decisionModal, setDecisionModal] = useState<{
    item: OrderPurchaseListItem;
    mode: "partial" | "none";
  } | null>(null);

  useEffect(() => {
    if (!orderId) {
      setOrder(null);
      return;
    }
    setLoading(true);
    fetchAdminOrder(orderId)
      .then(setOrder)
      .catch((error) => toast.error(getApiErrorMessage(error, "Não foi possível carregar o pedido")))
      .finally(() => setLoading(false));
  }, [orderId]);

  const applyUpdate = async (input: Parameters<typeof updatePurchaseListItem>[1], message: string) => {
    if (!order) return;
    try {
      const updated = await updatePurchaseListItem(order.id, input);
      setOrder(updated);
      onOrderChanged?.(updated);
      toast.success(message);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Não foi possível atualizar o item"));
    }
  };

  const markTotal = (item: OrderPurchaseListItem) =>
    applyUpdate(
      { productId: item.productId, unit: item.unit, purchased: true, purchasedQuantity: item.purchaseQuantity },
      "Item marcado como Total",
    );

  const markStock = (item: OrderPurchaseListItem) =>
    applyUpdate(
      { productId: item.productId, unit: item.unit, purchased: false, useStock: true },
      "Item marcado como Em estoque",
    );

  const submitAdvance = async (date: string) => {
    if (!order) return;
    try {
      const updated = await transitionOrder(order.id, {
        toStatus: "EM_PRODUCAO",
        transitionDate: date,
      });
      onOrderChanged?.(updated);
      toast.success(`${orderTitle(order)} → Em produção`);
      setAdvanceOpen(false);
      onClose();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Não foi possível avançar o pedido"));
    }
  };

  const pendingCount = order?.purchaseList.filter((item) => decisionOf(item) === "pending").length ?? 0;
  const decidedCount = (order?.purchaseList.length ?? 0) - pendingCount;
  const allDecided = order != null && order.purchaseList.length > 0 && pendingCount === 0;

  return (
    <>
      <Dialog open={orderId != null} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="flex max-h-[90vh] max-w-2xl flex-col overflow-hidden">
          <DialogHeader>
            <DialogTitle className="sr-only">Decisões de compra</DialogTitle>
            <DialogDescription className="sr-only">
              Decida cada item da lista de compra do pedido.
            </DialogDescription>
          </DialogHeader>

          {loading && (
            <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Carregando pedido...
            </div>
          )}

          {!loading && order && (
            <div className="flex min-h-0 flex-1 flex-col gap-4">
              <header className="shrink-0">
                <div className="flex items-baseline gap-3">
                  <span className="font-display text-lg font-semibold">{orderTitle(order)}</span>
                  <SubStatusBadge decided={decidedCount} total={order.purchaseList.length} />
                </div>
                <div className="mt-1 text-sm text-muted-foreground">
                  {CLIENT_TYPE_LABELS[order.clientType]} · entrega{" "}
                  <strong className="font-medium text-foreground">{order.deliveryWindow ?? "—"}</strong>
                </div>
                {order.notes && (
                  <div className="mt-3 rounded-lg border border-warning/30 bg-warning/5 p-3 text-sm">
                    <MessageCircle className="mr-1.5 inline h-3.5 w-3.5 align-[-2px] text-warning" />
                    <span className="font-medium">Observação: </span>
                    {order.notes}
                  </div>
                )}
              </header>

              <section className="flex min-h-0 flex-1 flex-col">
                <div className="mb-2 flex shrink-0 items-center justify-between">
                  <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Lista de compra
                  </h3>
                  <span className="text-xs text-muted-foreground">
                    {decidedCount}/{order.purchaseList.length} decididos
                    {pendingCount > 0 ? ` · ${pendingCount} pendentes` : ""}
                  </span>
                </div>
                <ul className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto pr-1">
                  {order.purchaseList.map((item) => {
                    const dec = decisionOf(item);
                    return (
                      <li
                        key={`${item.productId}:${item.unit}`}
                        className="rounded-md border border-border p-3"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="font-display text-sm font-semibold uppercase tracking-wide">
                              {item.productName}
                            </div>
                            <div className="mt-0.5 text-xs text-muted-foreground">
                              {item.purchaseQuantity} {item.unit}
                            </div>
                          </div>
                          <DecisionPill decision={dec} />
                        </div>

                        <div className="mt-3 flex flex-wrap gap-2">
                          <Button
                            size="sm"
                            variant={dec === "total" ? "hero" : "outline"}
                            onClick={() => void markTotal(item)}
                          >
                            <CheckCircle2 className="h-4 w-4" /> Total
                          </Button>
                          <Button
                            size="sm"
                            variant={dec === "partial" ? "hero" : "outline"}
                            onClick={() => setDecisionModal({ item, mode: "partial" })}
                          >
                            Parcial
                          </Button>
                          <Button
                            size="sm"
                            variant={dec === "stock" ? "hero" : "outline"}
                            onClick={() => void markStock(item)}
                          >
                            <Package className="h-4 w-4" /> Em estoque
                          </Button>
                          <Button
                            size="sm"
                            variant={dec === "none" ? "destructive" : "outline"}
                            onClick={() => setDecisionModal({ item, mode: "none" })}
                            className={cn(dec !== "none" && "text-destructive")}
                          >
                            <CircleSlash className="h-4 w-4" /> Não comprado
                          </Button>
                        </div>

                        {dec !== "pending" && (item.reason || item.purchasedQuantity != null) && (
                          <div className="mt-3 border-t border-border pt-2 text-xs text-muted-foreground">
                            {dec === "partial" && item.purchasedQuantity != null && (
                              <span>
                                <strong className="font-medium text-foreground">
                                  {item.purchasedQuantity} {item.unit}
                                </strong>{" "}
                                comprados ·{" "}
                              </span>
                            )}
                            {item.reason && <span>"{item.reason}"</span>}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </section>

              <footer className="flex shrink-0 items-center justify-between gap-3 border-t border-border bg-surface-elevated/40 -mx-6 -mb-6 px-6 py-3">
                <div className="text-xs text-muted-foreground">
                  Decida todos os itens antes de avançar.
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={onClose}>
                    Fechar
                  </Button>
                  <Button
                    variant="hero"
                    size="sm"
                    onClick={() => setAdvanceOpen(true)}
                    disabled={!allDecided}
                    title={!allDecided ? `Decida todos os itens (${pendingCount} pendentes)` : undefined}
                  >
                    <ArrowRight className="h-4 w-4" />
                    Avançar para Em produção
                    {pendingCount > 0 && ` (${pendingCount} pendentes)`}
                  </Button>
                </div>
              </footer>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AdvanceConfirmDialog
        open={advanceOpen}
        toStatusLabel="Em produção"
        pedidoLabel={order ? orderTitle(order) : ""}
        onCancel={() => setAdvanceOpen(false)}
        onConfirm={submitAdvance}
      />

      {decisionModal && (
        <PurchaseDecisionModal
          item={decisionModal.item}
          mode={decisionModal.mode}
          onCancel={() => setDecisionModal(null)}
          onSubmit={async ({ purchasedQuantity, reason }) => {
            await applyUpdate(
              {
                productId: decisionModal.item.productId,
                unit: decisionModal.item.unit,
                purchased: false,
                purchasedQuantity: decisionModal.mode === "partial" ? purchasedQuantity : undefined,
                reason,
              },
              decisionModal.mode === "partial" ? "Decisão parcial registrada" : "Item marcado como não comprado",
            );
            setDecisionModal(null);
          }}
        />
      )}
    </>
  );
}

function SubStatusBadge({ decided, total }: { decided: number; total: number }) {
  if (total === 0) return null;
  if (decided === 0) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-2 py-0.5 text-xs font-medium text-muted-foreground">
        <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground" /> Para comprar
      </span>
    );
  }
  if (decided === total) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-success/10 px-2 py-0.5 text-xs font-medium text-success-foreground">
        <CheckCircle2 className="h-3 w-3" /> Comprado
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-warning/10 px-2 py-0.5 text-xs font-medium text-warning-foreground">
      <span className="h-1.5 w-1.5 rounded-full bg-warning" /> Em negociação
    </span>
  );
}

function DecisionPill({ decision }: { decision: Decision }) {
  if (decision === "pending") {
    return (
      <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-[11px] text-muted-foreground">
        Pendente
      </span>
    );
  }
  const variants: Record<Exclude<Decision, "pending">, string> = {
    total: "bg-success/10 text-success-foreground",
    partial: "bg-warning/10 text-warning-foreground",
    stock: "bg-secondary text-foreground",
    none: "bg-destructive/10 text-destructive",
  };
  return (
    <span className={cn("inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium", variants[decision])}>
      {DECISION_LABEL[decision]}
    </span>
  );
}

// Silence unused import warning for Input — useful for future quantity editing inline.
void Input;
